import React from 'react';
import { HANDBUCH_FARBEN, HANDBUCH_SCHRIFT, SEITENVERHAELTNIS } from './stil';
import type { SeitenAusrichtung } from './stil';

/**
 * Die A3-Seite — Grundbaustein aller Handbuchkapitel.
 *
 * Portiert aus `components/document/A3Page.tsx` der Quell-App. Die Farb- und
 * Schriftwerte liegen daneben in `stil.ts`; warum sie NICHT ins globale
 * `src/index.css` gehen, steht dort.
 *
 * ═══ WARUM KOPF UND FUSS ALS PROP UND NICHT ALS IMPORT ═════════════════════
 * Die Quell-App laesst `A3Page` selbst `DocumentHeader` und `DocumentFooter`
 * einbinden und sich die Daten aus einem `Projekt`-Objekt greifen. Hier kommen
 * beide als `ReactNode` herein. Zwei Gruende: die Kapitel tragen verschiedene
 * Kopfzeilen (das Deckblatt hat gar keine), und die Datenquellen liegen in
 * OS-IA weit auseinander — Projektname im Report, Bauherr in `metadata`,
 * Vergabemodell in `handbuecher`. Ein Baustein, der sich seine Daten selbst
 * zusammensucht, muesste alle drei kennen.
 *
 * ═══ DIESE KONVENTION GILT FUER ALLE NEUN KAPITEL ══════════════════════════
 * Jedes Kapitel nimmt `kopf?: ReactNode` und `fuss?: ReactNode` und reicht
 * beides unveraendert hierher durch. KEIN Kapitel nimmt `seite`/`gesamtSeiten`
 * und baut sich Kopf oder Fuss selbst.
 *
 * Die Kapitel entstanden in zwei Wellen und waren zunaechst verschieden
 * geschnitten; entschieden wurde fuer diese Fassung, weil sie an drei Stellen
 * die richtigere ist:
 *
 * 1. DIE SEITENZAHL HAT NUR EINE QUELLE. „Seite 4 von 11" kennt allein
 *    `seitenPlan()` in `handbuchLogic.ts` — pure und geprueft. Nahmen die
 *    Kapitel `seite`/`gesamtSeiten`, muesste jeder Aufrufer (Bildschirm,
 *    PDF-Generator, Aushang) sie erneut durchreichen und koennte sich dabei
 *    verzaehlen. Eine Seitenzahl, die nicht aufgeht, stellt im Streitfall die
 *    Vollstaendigkeit des Dokuments in Frage.
 * 2. DIE KOPFDATEN WERDEN EINMAL EINGESAMMELT. Projektname, Bauherr,
 *    Vergabemodell, Stand und Fassung liegen in drei verschiedenen Quellen.
 *    Mit `kopf` als Knoten holt der Container sie einmal; mit
 *    `metadata`+`handbuch` je Kapitel taeten es neun Blaetter.
 * 3. SIE STEHT SCHON HIER. Dieser Baustein hat den Vertrag bereits, und ein
 *    Kapitel, das ihn nur weiterreicht, kann ihn nicht anders auslegen.
 */

interface Props {
    /** Kopfzeile der Seite, ueblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fusszeile der Seite, ueblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    ausrichtung?: SeitenAusrichtung;
    children: React.ReactNode;
}

/**
 * Eine Seite des Handbuchs.
 *
 * `overflow: hidden` ist Absicht und aus der Vorlage uebernommen: eine
 * gedruckte Seite kann nicht wachsen. Was nicht passt, faellt sichtbar ab —
 * und das soll man am Bildschirm sehen, bevor es im PDF auffaellt.
 */
export const A3Seite: React.FC<Props> = ({ kopf, fuss, ausrichtung = 'quer', children }) => (
    <div
        data-glass="blatt"
        className="mx-auto mb-8 w-full max-w-[1200px] flex flex-col overflow-hidden print:mb-0 print:border-0 print:shadow-none"
        style={{
            aspectRatio: SEITENVERHAELTNIS[ausrichtung],
            background: '#ffffff',
            border: `2px solid ${HANDBUCH_FARBEN.linieStark}`,
            padding: '18px 22px',
            fontFamily: HANDBUCH_SCHRIFT,
            fontSize: '9px',
            color: HANDBUCH_FARBEN.text,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
    >
        {kopf}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {children}
        </div>
        {fuss}
    </div>
);
